<template>
  <DepartmentsArea
    :localized="true"
    :disabled="false"
    :departments="departmentSource"
    :filteredDepartments="filteredDepartments"
    :selectedDepartmentIdStr="''"
    :useSmallMargin="true"
    :denyIdList="denyIdList"
    @selectDepartment="selectDepartment"
    @expandDepartment="expandDepartment"
    @collapseDepartment="collapseDepartment"
    @changeDepartmentsKeyword="changeDepartmentsKeyword"
  />
</template>
<script lang="ts">
import { defineComponent, ref, watch, type PropType } from "vue";
import DepartmentsArea from "./Departments/DepartmentsArea.vue";
import type { Department } from "./Departments/values/Department";
import type { FilteredDepartment } from "./Departments/values/FilteredDepartment";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "departments-tree",
  components: {
    DepartmentsArea,
  },
  props: {
    departments: { type: Array as PropType<Department[]>, required: true },
    denyIdList: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const filteredDepartments = ref<FilteredDepartment[]>([]);
    const departmentSource = ref<Department[]>([]);

    // ユーザーを登録する際、複数部署登録にも対応させるために区別IDを用いる
    const user_unique_number = ref(0);

    watch(
      () => props.departments,
      (value: Department[]): void => {
        departmentSource.value.splice(0, departmentSource.value.length);
        departmentSource.value.push(...cloneDeep(value));
      },
      { immediate: true }
    );

    // 部署を選択
    const selectDepartment = (id: string): void => {
      const department = props.departments.find((dep) => dep.departmentIdStr === id);
      emit("selectDepartment", department);
    };

    // 開く
    const expandDepartment = (id: string): void => {
      const index = departmentSource.value.findIndex(
        (department) => department.departmentIdStr == id
      );
      if (index < 0) return;

      const opened = departmentSource.value[index].opened;
      if (!opened) {
        openDepartment(departmentSource.value[index]);
      }
    };

    // 閉じる
    const collapseDepartment = (id: string): void => {
      const index = departmentSource.value.findIndex(
        (department) => department.departmentIdStr == id
      );
      if (index < 0) return;

      const opened = departmentSource.value[index].opened;
      if (opened) {
        closeDepartment(departmentSource.value[index]);
      }
    };

    // 検索ボックス入力
    const changeDepartmentsKeyword = (value: string): void => {
      if (!value) {
        filteredDepartments.value = [];
        return;
      }
      const hitDepartments = searchDepartments(value);
      setFilteredDepartments(hitDepartments);
    };

    // 開いた時の処理
    const openDepartment = (department: Department): void => {
      department.opened = true;

      const childDep = department.childIdStrList
        .map((id) => {
          return departmentSource.value.find((dep) => dep.departmentIdStr === id);
        })
        .filter((dep) => dep !== undefined) as Department[];

      childDep.forEach((child) => (child.visibled = true));
    };

    // 閉じた時の処理
    const closeDepartment = (department: Department): void => {
      department.opened = false;
      const target = sliceChildrenList(department);
      target.forEach((dep) => {
        // 子リスト全てを閉じて、非表示化
        dep.opened = false;
        dep.visibled = false;
      });
    };

    const searchDepartments = (keyword: string): Department[] => {
      const hitDepartments = departmentSource.value.filter((dep) => dep.name.indexOf(keyword) > -1);
      return hitDepartments;
    };

    const setFilteredDepartments = (deps: Department[]): void => {
      filteredDepartments.value = deps.map((dep) => {
        return {
          name: dep.name,
          departmentIdStr: dep.departmentIdStr,
          userCount: dep.userCount,
          depth: dep.depth,
          fullPathWithoutRoot: "",
        };
      });
    };

    // 該当部署の子リストを返す (子共の子供の...等も全て含む)
    const sliceChildrenList = (department: Department): Department[] => {
      const index = departmentSource.value.findIndex(
        (dep) => dep.departmentIdStr == department.departmentIdStr
      );
      if (index < 0) return [];
      // 該当の部署より下のindexを切り出し
      const filtered = departmentSource.value.slice(index + 1);

      // さらに子要素を切り出し
      const results: Department[] = [];
      for (let i = 0; i < filtered.length; i++) {
        const dep = filtered[i];
        if (dep.depth > department.depth) {
          results.push(dep);
        } else {
          break;
        }
      }
      return results;
    };

    return {
      filteredDepartments,
      departmentSource,
      user_unique_number,
      selectDepartment,
      expandDepartment,
      collapseDepartment,
      changeDepartmentsKeyword,
      openDepartment,
      closeDepartment,
      searchDepartments,
      setFilteredDepartments,
      sliceChildrenList,
    };
  },
});
</script>
